// services/contactService.ts

import apiClient from "../../core/apiClient";

import { Contact } from "../../interfaces/DTO/crm/contact";
import { FileMetadataDTO, StoredFileDTO } from "../../interfaces/DTO/storageFile";
import { ContactPortalLinkResponse } from "../../interfaces/DTO/public/contactPortal";
import { BaseService } from "../BaseService";


const API_URL = "/contacts";

class ContactService extends BaseService<Contact> {
  constructor() {
    super(API_URL);
  }
async uploadFile(
  contactId: number,
  file: File,
  customName?: string,
  metadata?: FileMetadataDTO
): Promise<{ file_url: string }> {
  const form = new FormData();
  form.append("file", file);
  if (customName) form.append("file_name", customName);

  if (metadata) {
    if (metadata.system_data_type) {
      form.append("system_data_type", metadata.system_data_type);
    }
    if (metadata.target_handler_code) {
      form.append("target_handler_code", metadata.target_handler_code);
    }
    if (metadata.custom_tag) {
      form.append("custom_tag", metadata.custom_tag);
    }
  }

  const response = await apiClient.post(`${API_URL}/${contactId}/upload-file`, form);
  return response.data;
}
async updateFileMetadata(
      contactId: number,
      full_path: string,
      metadata: FileMetadataDTO
    ): Promise<void> {
      const form = new FormData();
      form.append("full_path", full_path);

      // שדה חובה
      if (metadata.system_data_type) {
        form.append("system_data_type", metadata.system_data_type);
      }

      // שדות אופציונליים - שולחים תמיד כדי לאפשר מחיקה
      form.append("target_handler_code", metadata.target_handler_code || "");
      form.append("custom_tag", metadata.custom_tag || "");

      await apiClient.patch(`${API_URL}/${contactId}/update-metadata`, form);
    }

  async listFiles(contactId: number): Promise<StoredFileDTO[]> {
    const res = await apiClient.get(`${API_URL}/${contactId}/files`);
    return res.data;
  }

  async listInsuranceFiles(contactId: number): Promise<StoredFileDTO[]> {
    const res = await apiClient.get(`${API_URL}/${contactId}/insurance-files`);
    return res.data;
  }
  async getContactFileStreamUrl(contactId: number, fullPath: string): Promise<string> {
    const encodedPath = encodeURIComponent(fullPath);
    return `/contacts/${contactId}/stream-file?full_path=${encodedPath}`;
  }
  async getContactFileDownloadUrl(contactId: number, fullPath: string): Promise<string> {
    const response = await apiClient.post(`/contacts/${contactId}/download-url`, {
      full_path: fullPath,
    });
    return response.data.signed_url;
  }

  async deleteFile(contactId: number, fullPath: string): Promise<void> {
    await apiClient.delete(`${API_URL}/${contactId}/file`, {
      params: { full_path: fullPath },
    });
  }

  async createPortalLink(contactId: number, expiryDays = 7): Promise<ContactPortalLinkResponse> {
    const res = await apiClient.post(`${API_URL}/${contactId}/portal-link`, null, {
      params: { expiry_days: expiryDays },
    });
    return res.data;
  }

  // Generic document extraction (supports all document types via registry)
  async extractDocument(contactId: number, subjectRef: string): Promise<any> {
    const response = await apiClient.post(
      `${API_URL}/${contactId}/documents/extract`,
      { subject_ref: subjectRef }
    );
    return response.data;
  }

  // Get existing artifact for a document (supports all document types)
  async getDocumentArtifact(contactId: number, subjectRef: string): Promise<any> {
    const response = await apiClient.post(
      `${API_URL}/${contactId}/documents/artifact`,
      { subject_ref: subjectRef }
    );
    return response.data;
  }

  // Get supported document types for AI extraction
  async getSupportedDocumentTypes(contactId: number): Promise<any> {
    const response = await apiClient.get(
      `${API_URL}/${contactId}/documents/supported-types`
    );
    return response.data;
  }
}

export const contactService = new ContactService();
